/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { createContext, useCallback, useContext, useRef } from "react";

export interface IReferenceSelectorValue {
	value?: string;
	path?: string;
}

export interface IReferenceSelectorContext {
	getSelectedReference?: () => Promise<IReferenceSelectorValue>;
	selectReference?: (reference: IReferenceSelectorValue) => void;
}

const defaultValue: IReferenceSelectorContext = {};

export const ReferenceSelectorContext = createContext<IReferenceSelectorContext>(defaultValue);

export function useReferenceSelectorContext(): IReferenceSelectorContext {
	return useContext(ReferenceSelectorContext);
}

export interface IReferenceSelectorContextProviderProps {
	children?: React.ReactNode;
}

export const ReferenceSelectorContextProvider: React.FC<IReferenceSelectorContextProviderProps> = ({
	children
}) => {
	const currentPromise = useRef<{
		promise?: Promise<IReferenceSelectorValue>;
		resolve?: (value: IReferenceSelectorValue) => void;
	}>({});
	const getSelectedReference = useCallback<IReferenceSelectorContext["getSelectedReference"]>(() => {
		if (!currentPromise?.current?.promise) {
			currentPromise.current.promise = new Promise((resolve, reject) => {
				const timeout = setTimeout(() => {
					reject();
					clearTimeout(timeout);
					currentPromise.current = {};
				}, 200);

				currentPromise.current.resolve = (value: IReferenceSelectorValue) => {
					clearTimeout(timeout);
					resolve(value);

					currentPromise.current = {};
				};
			});
		}

		return currentPromise.current.promise;
	}, []);

	const selectReference = useCallback<IReferenceSelectorContext["selectReference"]>((reference) => {
		if (currentPromise.current?.resolve) {
			currentPromise.current.resolve(reference);
		}
	}, []);

	return (
		<ReferenceSelectorContext.Provider value={{ getSelectedReference, selectReference }}>
			{children}
		</ReferenceSelectorContext.Provider>
	);
};
